/**
 * Generic hook for declarative workflow feature visibility.
 *
 * Applies a set of `IFeatureVisibilityRule` rules whenever:
 * - The active workflow step changes
 * - A display feature is added or removed (covers TEP overlay appearing/disappearing)
 *
 * Rules are evaluated with the current step ID and a `hasFeature` predicate,
 * so visibility decisions can depend on whether other features are present.
 *
 * Usage: call once in ViewerContent with `config.featureVisibility ?? []`.
 * The hook is a no-op when the rules array is empty.
 */

import { useEffect } from 'react';

import type { IFeatureVisibilityRule } from '../../types';

import { useVisualization, useWorkflowStep } from '@/context';
import { DisplayFeatureManager } from '@/visualization/display/display-feature-manager';
import { ViewMode } from '@/visualization/display/types';

export function useWorkflowFeatureVisibility(rules: readonly IFeatureVisibilityRule[]): void {
    const { activeStepId } = useWorkflowStep();
    const { getServiceManager } = useVisualization();

    useEffect(() => {
        if (rules.length === 0) return;

        let displayManager: DisplayFeatureManager;
        try {
            const sm = getServiceManager();
            displayManager = sm.getService<DisplayFeatureManager>(DisplayFeatureManager.SERVICE_NAME);
        } catch (error) {
            console.error('[useWorkflowFeatureVisibility] Failed to acquire DisplayFeatureManager:', error);
            return;
        }

        const ALL_VIEW_MODES = Object.values(ViewMode);

        const expandBooleanToMap = (visible: boolean): ReadonlyMap<ViewMode, boolean> => {
            const map = new Map<ViewMode, boolean>();
            for (const m of ALL_VIEW_MODES) map.set(m, visible);
            return map;
        };

        const applyVisibility = () => {
            const context = {
                stepId: activeStepId,
                hasFeature: (featureId: string) => displayManager.hasFeature(featureId),
            };

            for (const rule of rules) {
                const result = rule.isVisible(context);
                const visibility = typeof result === 'boolean' ? expandBooleanToMap(result) : result;
                displayManager.setViewModeVisibility(rule.featureId, visibility);
            }
        };

        applyVisibility();

        // Re-apply when TEP overlay (or any other feature) is added/removed
        const unsubFeatureAdded = displayManager.subscribe(
            displayManager.EVENTS.FEATURE_ADDED,
            applyVisibility
        );
        const unsubFeatureRemoved = displayManager.subscribe(
            displayManager.EVENTS.FEATURE_REMOVED,
            applyVisibility
        );

        return () => {
            unsubFeatureAdded();
            unsubFeatureRemoved();
        };
    }, [activeStepId, getServiceManager, rules]);
}
